import _ from 'lodash'
import URI from 'urijs'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
// import { useTranslation } from 'react-i18next'
import {
  useCategories,
  useDepartments,
  useDisableCheckouts,
} from '../../vendor/react-omnitech-api'
import CategoriesView from './categories-view'
// import getCategories from '../../helpers/get-categories-list'
import { useThemeConfig } from '../../hook/use-theme-config'
import { useAlert } from '../../hook/use-alert'
import { useCart } from '../../hook/use-cart'
import useDebounce from '../../hook/use-debounce'
import { useOrderMethod } from '../../hook/use-order-method'
import useSku from '../../hook/use-sku'
import { useLocation } from '../../hook/use-location'
import useOrderingPeriod from '../../hook/use-ordering-period'
import getTopLevelCategoryCode from '../../helpers/get-top-level-category-code'
import cancelRequest from '../../helpers/cancel-request'

const CategoriesController = (props) => {
  const {
    parentCode,
  } = props
  const menuFilterIntervalTimer = useRef(null)
  const deptmentsTimer = useRef(null)
  const categoriesTimer = useRef(null)
  const menuCodeFilterIntervalTimer = useRef(null)
  const [menuFilterDependency, setMenuFilterDependency] = useState('')
  const [menuCodeFilterDependency, setMenuCodeFilterDependency] = useState('')
  const [menuFiltersParam, setMenuFiltersParam] = useState({})
  const [menuCodeFiltersParam, setMenuCodeFiltersParam] = useState({})
  const [departments, setDepartments] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedDepartment, setSelectedDepartment] = useState()
  const [loading, setLoading] = useState(true)
  const [disableCheckouts, setDisableCheckouts] = useState()
  const [errorMissingMenuFilters, setErrorMissingMenuFilters] = useState(false)
  const [currentTimeSlots, setCurrentTimeSlots] = useState([])
  const alert = useAlert()
  const { getCart } = useCart()
  const {
    fetchDisableCheckoutsAll,
  } = useDisableCheckouts()
  const { getContentGroup, getConfig } = useThemeConfig()
  // const seoTitle = t('screens.categories.seo.title', '')
  const { fetchCategoriesAll } = useCategories()
  const { fetchDepartments } = useDepartments()
  const { orderMethod, store } = useOrderMethod()
  const {
    getMetaMenuFilterParams,
    getMetaMenuCodeFilterParamsAsync,
  } = useSku()
  const { location } = useLocation()
  const { getCurrentSlotCodesAsync } = useOrderingPeriod()

  const orderingCategoriesSortingBySlotCode = useCallback((_categories) => (
    _.sortBy(_categories, ({ meta }) => {
      const numOfMatches = _.size(_.intersection(_.get(meta, 'firstPrioritySlotCode', []), currentTimeSlots))
      if (numOfMatches > 0) {
        return numOfMatches * -1
      }
      return 0
    })
  ), [currentTimeSlots])

  const commerceChannelFromUrl = useMemo(() => {
    const url = new URI(_.get(location, 'href'))
    const search = url.search(true)
    return _.get(search, 'cc')
  }, [location])
  const commerceChannelFromOrderMethod = useMemo(() => (
    _.get(orderMethod, 'commerceChannel')
  ), [orderMethod])
  const orderMethodCode = useMemo(() => (
    _.get(orderMethod, 'code')
  ), [orderMethod])
  const commerceChannelReady = useMemo(() => (
    _.isEqual(
      commerceChannelFromUrl,
      commerceChannelFromOrderMethod,
    ) || _.isEmpty(commerceChannelFromUrl)
  ), [commerceChannelFromUrl, commerceChannelFromOrderMethod])
  const cart = useMemo(() => getCart(), [getCart])
  const showDineInOrderPlaced = useMemo(() => (
    _.get(orderMethod, 'code') === 'dineIn'
  ), [orderMethod])
  const topCategoryCode = useMemo(() => getTopLevelCategoryCode({
    topLevelCategory: _.get(orderMethod, 'topLevelCategory'),
    storeCode: _.get(store, 'code', ''),
  }), [orderMethod, store])
  const parentCodeEq = useMemo(() => (
    parentCode || topCategoryCode
  ), [parentCode, topCategoryCode])
  const inventoryStore = useMemo(() => {
    if (!_.isEmpty(store)) return store
    return _.get(cart, 'cartShipments.0.inventoryStore')
  }, [cart, store])
  const inventoryStoreCode = useMemo(() => (
    _.get(inventoryStore, 'code')
  ), [inventoryStore])
  const inventoryStoreId = useMemo(() => (
    _.get(inventoryStore, 'id')
  ), [inventoryStore])

  const checkCheckoutDisabled = useMemo(() => (
    getConfig('config.pages.categories.checkCheckoutDisabled', false)
  ), [getConfig])
  const checkoutDisabled = useMemo(() => {
    if (!_.isArray(disableCheckouts)) return disableCheckouts
    return (
      !_.isEmpty(
        _.find(
          disableCheckouts,
          {
            commerceChannel: commerceChannelFromOrderMethod,
            deliveryType: _.get(cart, 'cartShipments.0.deliveryType.code'),
          },
        ),
      )
    )
  }, [disableCheckouts, commerceChannelFromOrderMethod, cart])

  const bannerContentGroupProps = useMemo(() => (
    getContentGroup('config.pages.categories.banner')
  ), [getContentGroup])

  const updateMenuFilterDependency = () => {
    const params = getMetaMenuFilterParams({
      prefix: 'skus',
      storeCode: inventoryStoreCode,
    })
    const dependency = _.join(
      _.sortedUniq(
        _.flatten(
          _.values(params),
        ),
      ),
      ',',
    )
    setMenuFiltersParam(params)
    setMenuFilterDependency(dependency)
  }

  const updateMenuCodeFilterDependency = async () => {
    try {
      const params = await getMetaMenuCodeFilterParamsAsync({
        prefix: 'skus',
        storeId: inventoryStoreId,
      })
      const dependency = _.join(
        _.sortedUniq(
          _.flatten(
            _.values(params),
          ),
        ),
        ',',
      )
      setMenuCodeFiltersParam(params)
      setMenuCodeFilterDependency(dependency)
    } catch (error) {
      // fail silently
    }
  }

  /**
   * fetchCategoriesApi
   * get product data from API
   */
  const fetchCategoriesApi = async () => {
    setLoading(true)
    try {
      // Explode if no menuFilter params or no store and we're in dineIn mode.
      if (_.get(orderMethod, 'code') === 'dineIn' && (_.isEmpty(menuFiltersParam) || _.isEmpty(inventoryStore))) {
        throw new Error('ERR_MISSING_MENU_FILTER')
      }
      // api call option
      const option = _.omitBy({
        // id,
        includes: [
          'meta',
          // 'parent',
          'children',
        ].join(','),
        ...(
          _.isEmpty(parentCodeEq)
            ? { parentNull: true }
            : { parentCodeEq }
        ),
        // parentCodeEq: 'food_menu_single',
        // departmentCodeEq: 'SME',
        skuActive: true,
        'skus|product_type_eq': 'product',
        ...menuFiltersParam,
        ...menuCodeFiltersParam,
        arrayFormat: 'brackets',
      }, _.isNil)
      // call api
      const { categories: data } = await fetchCategoriesAll(option)
      setCategories(data)
    } catch (error) {
      if (_.get(error, 'message') === 'ERR_MISSING_MENU_FILTER') {
        setErrorMissingMenuFilters(true)
      } else {
        const generalError = _.get(error, 'generalError', {})
        // if (generalError.code === 404) {
        //   recordNotFound(currentPage)
        //   return
        // }
        alert.show(generalError.message, { state: 'error' })
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * fetchDepartmentsApi
   * get product data from API
   */
  const fetchDepartmentsApi = async () => {
    cancelRequest.cancelAll(['fetchDepartments'])
    setLoading(true)
    try {
      // api call option
      const option = _.omitBy({
        includes: [
          'categories.children',
          'categories.meta',
          'categories',
        ].join(','),
        skuActive: true,
        'skus|product_type_eq': 'product',
        ...menuFiltersParam,
        ...menuCodeFiltersParam,
        arrayFormat: 'brackets',
        pageSize: 999,
      }, _.isNil)
      // call api
      const { departments: data } = await fetchDepartments(option)
      setDepartments(data)
      setSelectedDepartment(_.get(_.first(data), 'code'))
    } catch (error) {
      const generalError = _.get(error, 'generalError', {})
      alert.show(generalError.message, { state: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const fetchCheckoutDisabledApi = async () => {
    try {
      const { disableCheckouts: disableCheckoutsData } = await fetchDisableCheckoutsAll({
        storeId: _.get(store, 'id'),
        commerceChannelCodeEq: commerceChannelFromOrderMethod,
      })
      setDisableCheckouts(disableCheckoutsData)
    } catch (error) {
      const generalError = _.get(error, 'generalError', {})
      alert.show(generalError.message, { state: 'error' })
    }
  }

  const updateCurrentTimeSlots = async () => {
    try {
      const timeSlots = await getCurrentSlotCodesAsync()
      setCurrentTimeSlots(timeSlots)
    } catch (error) {
      const generalError = _.get(error, 'generalError', {})
      alert.show(generalError.message, { state: 'error' })
    }
  }

  const onDepartmentClick = (code) => {
    setSelectedDepartment(code)
  }

  useEffect(() => {
    updateMenuFilterDependency()
    updateMenuCodeFilterDependency()
    menuFilterIntervalTimer.current = setInterval(() => {
      updateMenuFilterDependency()
    }, 1000 * 10) // update menu filter dependency every 10 seconds
    menuCodeFilterIntervalTimer.current = setInterval(() => {
      updateMenuCodeFilterDependency()
    }, 1000 * 60 * 10) // update menu code filter dependency every 10 minutes

    return () => {
      clearInterval(menuFilterIntervalTimer.current)
      clearInterval(menuCodeFilterIntervalTimer.current)
      cancelRequest.cancelAll(['fetchCategories'])
    }
  }, [inventoryStoreCode])

  useEffect(() => {
    if (!commerceChannelReady) return
    if (checkCheckoutDisabled) {
      fetchCheckoutDisabledApi()
    } else {
      setDisableCheckouts([])
    }
  }, [checkCheckoutDisabled, commerceChannelReady])

  useEffect(() => {
    // Wait for commerceChannel change
    // and no disable checkouts match
    if (
      commerceChannelReady
      && checkoutDisabled === false
    ) {
      clearTimeout(deptmentsTimer.current)
      clearTimeout(categoriesTimer.current)
      setDepartments([])
      setCategories([])
      if (_.isEmpty(parentCodeEq)) {
        deptmentsTimer.current = setTimeout(fetchDepartmentsApi, 500)
      } else {
        categoriesTimer.current = setTimeout(fetchCategoriesApi, 500)
      }
    }
  }, [
    menuFilterDependency,
    menuCodeFilterDependency,
    parentCodeEq,
    commerceChannelReady,
    checkoutDisabled,
    orderMethodCode,
  ])

  useEffect(() => {
    const selectedDept = _.find(departments, { code: selectedDepartment })
    setCategories(
      _.get(selectedDept, 'categories', []),
    )
  }, [selectedDepartment, departments])

  useEffect(useDebounce(() => {
    updateCurrentTimeSlots()
  }, 1000), [
    menuFilterDependency,
    menuCodeFilterDependency,
  ])

  const viewProps = {
    // pageReady,
    // seoTitle,
    checkoutDisabled,
    loading,
    categories: orderingCategoriesSortingBySlotCode(categories),
    departments,
    bannerContentGroupProps,
    onDepartmentClick,
    selectedDepartment,
    showDineInOrderPlaced,
    errorMissingMenuFilters,
  }

  return (
    <CategoriesView {...viewProps} />
  )
}

export default CategoriesController
